import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/miscellaneous/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useGetUserQuery } from "@/hooks/queries/useGetUserQuery";
import { useGetRolesQuery } from "@/hooks/queries";

// Mirrors TS-loom UserSection: 32px square avatar + name + [ ROLE ] + caret,
// with a [ SIGN OUT ] dropdown.
export function ProfileMenu() {
  const queryClient = useQueryClient();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { data: user } = useGetUserQuery();
  const { data: roleData } = useGetRolesQuery();

  if (!user) return null;

  const displayName =
    `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
    user.email.split("@")[0];
  const role = roleData?.isSuperAdmin || roleData?.isAdmin ? "ADMIN" : "USER";
  const initials = displayName.slice(0, 2).toUpperCase();

  const handleLogout = () => {
    queryClient.clear();
    logout().then(() => {
      navigate("/");
    });
  };

  return (
    <div className="flex items-center gap-4">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="User menu"
            className="flex items-center gap-3 rounded-none px-2 py-1 outline-none focus-visible:border focus-visible:border-[var(--color-accent)]"
          >
            <span className="inline-flex h-8 w-8 items-center justify-center border border-[var(--color-border)] bg-[var(--color-surface)] font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--color-text)]">
              {initials}
            </span>
            <div className="hidden flex-col items-start leading-tight md:flex">
              <span className="text-[14px] font-medium text-[var(--color-text)]">
                {displayName}
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                [ {role} ]
              </span>
            </div>
            <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
              ▾
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          sideOffset={8}
          align="end"
          className="z-50 min-w-[180px] rounded-none border border-[var(--color-border)] bg-[var(--color-bg)] p-1"
        >
          <DropdownMenuItem
            onClick={handleLogout}
            className="flex w-full items-center justify-between rounded-none px-3 py-3 text-left font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--color-text-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-accent)]"
          >
            <span>[ SIGN OUT ]</span>
            <span>→</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
